import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X, Upload, Star } from "lucide-react";
import { resolveImageUrl } from "@/utils/imageUrl";
import { CATEGORIES } from "@/utils/adminHelpers";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inputCls = "w-full bg-[#050505] border border-white/10 px-3 py-2.5 text-white rounded-xl focus:border-[#00FF66] focus:outline-none transition-colors";

const Field = ({ label, children, hint }) => (
  <div>
    <label className="block text-xs text-[#A0A0A0] mb-2 uppercase tracking-wider">{label}</label>
    {children}
    {hint && <p className="text-xs text-[#A0A0A0] mt-1">{hint}</p>}
  </div>
);

const ImageThumb = ({ src, index, primary, onRemove }) => (
  <div className="relative rounded-xl overflow-hidden aspect-square">
    <img src={resolveImageUrl(src)} alt={`Foto ${index}`} className="w-full h-full object-cover" />
    <span className={`absolute top-1 left-1 text-[8px] px-1.5 py-0.5 rounded-full font-bold ${primary ? "bg-[#00FF66] text-[#050505]" : "bg-white/20 text-white"}`}>{index}</span>
    <button type="button" onClick={onRemove} className="absolute top-1 right-1 bg-red-500/80 text-white p-0.5 rounded-full"><X className="w-3 h-3" /></button>
  </div>
);

export const ProductFormModal = ({ formData, setFormData, isEdit, exchangeRate, toUSD, onSubmit, onClose }) => {
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const set = (patch) => setFormData(prev => ({ ...prev, ...patch }));

  const handleUsdChange = (val) => {
    const mxn = ((parseFloat(val) || 0) * exchangeRate).toFixed(2);
    set({ buy_price_usd: val, buy_price: mxn });
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("Max 5MB"); return; }
    setUploading(true);
    const uploadData = new FormData();
    uploadData.append("file", file);
    try {
      const res = await axios.post(`${API}/upload`, uploadData, { headers: { "Content-Type": "multipart/form-data" } });
      const url = res.data.image_url;
      setFormData(prev => (prev.image_url ? { ...prev, images: [...(prev.images || []), url] } : { ...prev, image_url: url }));
      toast.success("Imagen subida");
    } catch {
      toast.error("Error al subir imagen. Intenta de nuevo.");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const removePrimary = () => setFormData(prev => {
    const [next, ...rest] = prev.images || [];
    return next ? { ...prev, image_url: next, images: rest } : { ...prev, image_url: "" };
  });
  const removeExtra = (index) => setFormData(prev => ({ ...prev, images: (prev.images || []).filter((_, i) => i !== index) }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      await onSubmit();
    } catch {
      toast.error("Error al guardar. Intenta de nuevo.");
    } finally {
      setSubmitting(false);
    }
  };

  const profitPreview = parseFloat(formData.sale_price || 0) - parseFloat(formData.buy_price || 0);
  const hasImages = formData.image_url || formData.images?.length > 0;
  let submitLabel = "Crear";
  if (submitting) submitLabel = "Guardando...";
  else if (isEdit) submitLabel = "Guardar";

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="glass-card w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b border-white/5">
          <h2 className="font-oswald font-semibold text-lg">{isEdit ? "Editar Producto" : "Nuevo Producto"}</h2>
          <button onClick={onClose} className="text-[#A0A0A0] hover:text-white" data-testid="product-form-close"><X className="w-5 h-5" /></button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-4" data-testid="product-form">
          <Field label="Imagenes">
            {hasImages && (
              <div className="grid grid-cols-3 gap-2 mb-3">
                {formData.image_url && <ImageThumb src={formData.image_url} index={1} primary onRemove={removePrimary} />}
                {(formData.images || []).map((img, idx) => (
                  <ImageThumb key={`img-${idx}-${img.slice(-10)}`} src={img} index={idx + 2} onRemove={() => removeExtra(idx)} />
                ))}
              </div>
            )}
            <label className="block border-2 border-dashed border-white/10 p-6 text-center cursor-pointer hover:border-[#00FF66]/50 transition-colors rounded-xl">
              <input type="file" onChange={handleImageUpload} accept="image/*" className="hidden" data-testid="input-image-file" />
              {uploading ? (
                <div className="flex flex-col items-center gap-2">
                  <div className="w-6 h-6 border-2 border-[#00FF66] border-t-transparent rounded-full animate-spin" />
                  <span className="text-sm text-[#A0A0A0]">Subiendo...</span>
                </div>
              ) : (
                <div className="flex flex-col items-center gap-1">
                  <Upload className="w-6 h-6 text-[#A0A0A0]/50" />
                  <span className="text-xs text-[#A0A0A0]">{formData.image_url ? "Agregar otra foto" : "Subir foto"}</span>
                </div>
              )}
            </label>
          </Field>

          <Field label="Nombre">
            <input type="text" value={formData.name} onChange={(e) => set({ name: e.target.value })} className={inputCls} placeholder="Nombre del producto" required data-testid="input-name" />
          </Field>
          <Field label="Marca">
            <input type="text" value={formData.brand} onChange={(e) => set({ brand: e.target.value })} className={inputCls} placeholder="Nike, Adidas, etc." data-testid="input-brand" />
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Tallas">
              <input type="text" value={formData.size} onChange={(e) => set({ size: e.target.value })} className={inputCls} placeholder="S, M, L" required data-testid="input-size" />
            </Field>
            <Field label="Categoria">
              <select value={formData.category} onChange={(e) => set({ category: e.target.value })} className={inputCls} data-testid="input-category">
                {CATEGORIES.map(cat => <option key={cat.value} value={cat.value}>{cat.label}</option>)}
              </select>
            </Field>
          </div>

          <label className="flex items-center gap-3 cursor-pointer group" data-testid="exclusive-toggle">
            <div className="relative">
              <input type="checkbox" checked={formData.exclusive} onChange={(e) => set({ exclusive: e.target.checked })} className="sr-only peer" />
              <div className="w-10 h-5 rounded-full bg-white/10 peer-checked:bg-amber-400/30 transition-colors" />
              <div className="absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-[#A0A0A0] peer-checked:bg-amber-400 peer-checked:translate-x-5 transition-all" />
            </div>
            <div className="flex items-center gap-1.5">
              <Star className={`w-4 h-4 ${formData.exclusive ? "text-amber-400 fill-amber-400" : "text-[#A0A0A0]"}`} />
              <span className={`text-sm ${formData.exclusive ? "text-amber-400" : "text-[#A0A0A0]"}`}>Exclusivo</span>
            </div>
          </label>

          <Field label="Costo (USD)" hint={`Tasa: 1 USD = $${exchangeRate.toFixed(2)} MXN`}>
            <div className="flex items-center gap-2">
              <input type="number" step="0.01" value={formData.buy_price_usd} onChange={(e) => handleUsdChange(e.target.value)} className={`flex-1 ${inputCls}`} placeholder="25" data-testid="input-buy-price-usd" />
              <span className="text-[#A0A0A0] text-sm">=</span>
              <input type="number" step="0.01" value={formData.buy_price} onChange={(e) => set({ buy_price: e.target.value })} className={`flex-1 ${inputCls}`} placeholder="430" data-testid="input-buy-price" />
              <span className="text-[#A0A0A0] text-sm">MXN</span>
            </div>
          </Field>

          <Field label="Precio Original Tienda (opcional)" hint="Precio en tienda para mostrar ahorro al cliente">
            <input type="number" step="0.01" value={formData.retail_price} onChange={(e) => set({ retail_price: e.target.value })} className={inputCls} placeholder="850" data-testid="input-retail-price" />
          </Field>
          <Field label="Precio de Venta (MXN)">
            <input type="number" step="0.01" value={formData.sale_price} onChange={(e) => set({ sale_price: e.target.value })} className={inputCls} placeholder="480" required data-testid="input-sale-price" />
          </Field>

          {formData.buy_price && formData.sale_price && (
            <div className="bg-[#050505] border border-white/5 rounded-xl p-3 text-center" data-testid="profit-preview">
              <span className="text-xs text-[#A0A0A0]">Ganancia: </span>
              <span className={`font-oswald font-bold ${profitPreview >= 0 ? "text-[#00FF66]" : "text-red-400"}`}>${profitPreview.toFixed(2)} MXN</span>
              <span className="text-xs text-[#A0A0A0] ml-2">(${toUSD(profitPreview)} USD)</span>
            </div>
          )}

          <div className="flex gap-3 pt-4">
            <button type="button" onClick={onClose} className="flex-1 border border-white/10 py-3 text-[#A0A0A0] hover:text-white hover:border-white/30 rounded-full transition-all duration-300">Cancelar</button>
            <button type="submit" disabled={submitting} className="flex-1 bg-[#00FF66] text-[#050505] py-3 font-bold rounded-full hover:shadow-[0_0_16px_rgba(0,255,102,0.4)] transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed" data-testid="submit-product-btn">{submitLabel}</button>
          </div>
        </form>
      </div>
    </div>
  );
};
